// Konfetti beim Einmal-Overlay nach einem Update (Wunsch C.Firchau, v3.0.1),
// seit #608 aus den unteren Ecken des Dialogs ins Bild geschossen wie 🎉,
// angelehnt an Open WebUI. Bewusst ohne Paket: ein Canvas über der ganzen
// Seite, das keine Klicks abfängt und sich nach dem letzten Teilchen selbst
// entfernt.
//
// Kein Konfetti bei „Bewegung reduzieren“ (prefers-reduced-motion) und dort,
// wo es kein matchMedia oder kein Canvas gibt (Tests, alte Browser).
// Die Farben stehen als Token in themes/kaycee.css (--kc-ticket-konfetti),
// eine Liste mit Kommas, weil der Farbdeckel Literale nur dort zulässt.

const DAUER_MS = 4500;
const LUFT = 0.97;       // Luftwiderstand je Bild: bremst den Schuss, oben „stehen“ sie kurz
const SCHWERKRAFT = 0.2;
const MAX_FALL = 4;      // langsames Rieseln statt Fallen

/**
 * #608: Eine Salve je Ecke, wie 🎉. Die Teilchen starten an der Ecke und
 * fliegen schräg nach oben zur Mitte (`richtung` 1 = nach rechts, -1 = nach
 * links), 10–60° gegen die Senkrechte gestreut. `staerke` skaliert die
 * Geschwindigkeit mit der Höhe, die über der Ecke frei ist.
 */
export function erzeugeTeilchen(anzahl, ecken, farben, { staerke = 1, zufall = Math.random } = {}) {
  const liste = ecken.length ? ecken : [{ x: 0, y: 0, richtung: 1 }];
  return Array.from({ length: anzahl }, (_, i) => {
    const ecke = liste[i % liste.length];
    const winkel = (10 + zufall() * 50) * Math.PI / 180;
    const tempo = (16 + zufall() * 16) * staerke;
    return {
      x: ecke.x,
      y: ecke.y,
      vx: ecke.richtung * Math.sin(winkel) * tempo,
      vy: -Math.cos(winkel) * tempo,
      winkel: zufall() * Math.PI * 2,
      drehung: (zufall() - 0.5) * 0.3,
      groesse: 6 + zufall() * 6,
      farbe: farben[Math.floor(zufall() * farben.length)] || farben[0]
    };
  });
}

/** Ein Zeitschritt (dt in Bildern zu 1/60 s): Luftwiderstand, Schwerkraft, Drehung. */
export function schritt(teilchen, dt = 1) {
  const bremse = Math.pow(LUFT, dt);
  for (const t of teilchen) {
    t.vx *= bremse;
    t.vy = Math.min(t.vy * bremse + SCHWERKRAFT * dt, MAX_FALL);
    t.x += t.vx * dt;
    t.y += t.vy * dt;
    t.winkel += t.drehung * dt;
  }
  return teilchen;
}

/**
 * Die beiden Abschussorte: die unteren Ecken von `ziel` (dem Dialog), ohne
 * Ziel oder mit einem Ziel ohne Größe die unteren Ecken des Fensters.
 */
export function eckenVon(ziel, breite, hoehe) {
  let r = null;
  try {
    r = ziel && typeof ziel.getBoundingClientRect === 'function' ? ziel.getBoundingClientRect() : null;
  } catch (_) {
    r = null; // abgehängtes Element: wie ohne Ziel
  }
  if (!r || !(r.width > 0) || !(r.height > 0)) {
    return [{ x: 0, y: hoehe, richtung: 1 }, { x: breite, y: hoehe, richtung: -1 }];
  }
  return [{ x: r.left, y: r.bottom, richtung: 1 }, { x: r.right, y: r.bottom, richtung: -1 }];
}

function leseFarben() {
  try {
    const wert = getComputedStyle(document.documentElement).getPropertyValue('--kc-ticket-konfetti');
    const farben = String(wert || '').split(',').map((f) => f.trim()).filter(Boolean);
    if (farben.length) return farben;
  } catch (_) {
    // ohne Stile: Schriftfarbe als einzige Farbe
  }
  return ['currentColor'];
}

/**
 * Startet das Konfetti. Gibt eine Funktion zurück, die es vorzeitig beendet
 * (z. B. wenn das Overlay geschlossen wird, bevor alles gefallen ist).
 * `ziel`: Element, an dessen unteren Ecken es startet (#608).
 */
export function starteKonfetti({ anzahl = 160, dauerMs = DAUER_MS, ziel = null } = {}) {
  const nichts = () => {};
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return nichts;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return nichts;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) return nichts;

  const breite = window.innerWidth;
  const hoehe = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;
  canvas.width = breite * dpr;
  canvas.height = hoehe * dpr;
  canvas.setAttribute('aria-hidden', 'true');
  canvas.className = 'konfetti';
  ctx.scale(dpr, dpr);
  document.body.appendChild(canvas);

  const ecken = eckenVon(ziel, breite, hoehe);
  // Schusshöhe nach dem Platz über den Ecken: bis knapp unter den oberen Rand
  const staerke = Math.max(0.6, Math.min(1.4, ecken[0].y / 650));
  const teilchen = erzeugeTeilchen(anzahl, ecken, leseFarben(), { staerke });
  const start = performance.now();
  let zuletzt = start;
  let bild = null;
  let beendet = false;

  const ende = () => {
    if (beendet) return;
    beendet = true;
    if (bild !== null) cancelAnimationFrame(bild);
    canvas.remove();
  };

  const male = (jetzt) => {
    const dt = Math.min((jetzt - zuletzt) / (1000 / 60), 3);
    zuletzt = jetzt;
    schritt(teilchen, dt);
    // Zum Schluss ausblenden statt abrupt verschwinden
    const rest = 1 - (jetzt - start) / dauerMs;
    ctx.clearRect(0, 0, breite, hoehe);
    ctx.globalAlpha = Math.max(0, Math.min(1, rest * 3));
    for (const t of teilchen) {
      ctx.save();
      ctx.translate(t.x, t.y);
      ctx.rotate(t.winkel);
      ctx.fillStyle = t.farbe;
      ctx.fillRect(-t.groesse / 2, -t.groesse / 4, t.groesse, t.groesse / 2);
      ctx.restore();
    }
    if (rest <= 0) { ende(); return; }
    bild = requestAnimationFrame(male);
  };
  bild = requestAnimationFrame(male);
  return ende;
}
